import { CloseRequestContext } from 'src/modules/shared/contexts/close-request-context';
// src/modules/shared/components/molecules/modals/modal/index.tsx

import React, { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from 'src/modules/shared/hooks/modal';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PiXLight } from 'react-icons/pi';
import Button from '../../../atoms/buttons/button';
import { Separator } from '../../../atoms/separator';

import type { ModalProps } from 'src/modules/shared/types/modals/modal.type';

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  header,
  body,
  footer,
  dialogClassName = '',
  headerClassName = '',
  bodyClassName = '',
  footerClassName = '',
  ariaLabel,
  closeLabel,
  dismissible = true,
  compact = false,
  showCloseButton = false,
  returnFocusRef,
}) => {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeLayerId = useModal({
    isOpen,
    onClose,
    dialogRef,
    dismissible,
    returnFocusRef,
  });
  const headerId = useId();
  const resolvedCloseLabel = closeLabel ?? t('shared.actions.close');
  const resolvedAriaLabel =
    ariaLabel ?? (header ? undefined : t('shared.modal.dialogLabel'));
  const labelledBy = ariaLabel || !header ? undefined : headerId;

  if (!isOpen) return null;

  /** Material UI z-index scale puts modal at 1300 */
  const overlayClasses = `
    fixed inset-0 z-[1300] flex items-center justify-center ${compact ? 'p-4' : 'p-0 md:p-4'}
    max-w-full max-h-full overflow-auto
  `;
  const backdropClasses = 'fixed inset-0 bg-black opacity-50';
  const dialogClasses = `
    relative min-h-0 overflow-hidden bg-white shadow-xl flex w-full flex-col z-[1400]
    ${compact ? 'h-auto max-h-[calc(100dvh-2rem)] max-w-md rounded-lg' : 'rounded-none h-[100dvh] max-h-[100dvh] max-w-full sm:h-auto sm:max-h-[80%] sm:max-w-2xl sm:rounded-lg'} ${dialogClassName}
  `;
  const headerClasses = `
    flex-none flex px-4 py-2 sm:py-3 items-center justify-between ${headerClassName}
  `;
  const bodyClasses = `
    min-h-0 flex-1 flex ${compact ? 'px-4 py-3' : 'px-2 md:py-2 md:px-4'} overflow-auto ${bodyClassName}
  `;
  const footerClasses = `
    flex-none justify-end ${footerClassName}
  `;

  const modalContent = (
    <div className={overlayClasses}>
      <div
        className={backdropClasses}
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      ></div>

      <div
        ref={dialogRef}
        className={dialogClasses}
        role="dialog"
        aria-modal="true"
        aria-label={resolvedAriaLabel}
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        {!header && dismissible && showCloseButton && (
          <Button
            onClick={onClose}
            variant="neutral"
            appearance="ghost"
            size="sm"
            shape="round"
            isIconOnly
            className="absolute right-2 top-2 z-10"
            aria-label={resolvedCloseLabel}
          >
            <PiXLight className="h-4 w-4" />
          </Button>
        )}
        {header && (
          <>
            <div className={headerClasses}>
              <div id={headerId} className="min-w-0 flex-1">
                {header}
              </div>
              {dismissible ? (
                <Button
                  onClick={onClose}
                  variant="neutral"
                  appearance="ghost"
                  size="md"
                  isIconOnly
                  aria-label={resolvedCloseLabel}
                >
                  <PiXLight className="h-5 w-5" />
                </Button>
              ) : null}
            </div>
            <Separator padded />
          </>
        )}

        {body && <div className={bodyClasses}>{body}</div>}

        {footer && (
          <>
            <Separator padded />
            <div className={footerClasses}>{footer}</div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(
    <CloseRequestContext.Provider value={closeLayerId}>
      {modalContent}
    </CloseRequestContext.Provider>,
    document.body
  );
};

export default Modal;
